import { useEffect, useState } from 'react';
import type { FaqGroupSummary } from '../types';

const findInitialActive = (groups: ReadonlyArray<FaqGroupSummary>) => {
  if (typeof window === 'undefined') return groups[0]?.anchorId ?? '';
  const hash = window.location.hash.replace('#', '');
  const matched = groups.find((group) => group.anchorId === hash);
  return matched?.anchorId ?? groups[0]?.anchorId ?? '';
};

const observeGroups = (
  groups: ReadonlyArray<FaqGroupSummary>,
  setActiveAnchor: (anchorId: string) => void
) => {
  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      const top = visible[0];
      if (top?.target.id) setActiveAnchor(top.target.id);
    },
    {
      rootMargin: '-30% 0px -55% 0px',
      threshold: [0, 0.25, 0.5, 1]
    }
  );

  groups.forEach((group) => {
    const node = document.getElementById(group.anchorId);
    if (node) observer.observe(node);
  });

  return () => observer.disconnect();
};

export const useActiveAnchor = (groups: ReadonlyArray<FaqGroupSummary>) => {
  const [activeAnchor, setActiveAnchor] = useState<string>('');

  useEffect(() => {
    setActiveAnchor(findInitialActive(groups));
  }, [groups]);

  useEffect(() => {
    if (groups.length === 0) return;
    return observeGroups(groups, setActiveAnchor);
  }, [groups]);

  return activeAnchor;
};
